import React, { useEffect } from 'react'
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react'
import { AuthProvider } from './auth-provider'
import { InteractionType } from '@azure/msal-browser'
import { ILoginRequest } from '.'
import { UserMiddleware } from '../user-context/user-context-middleware'
import { LoadingOutlined, LockTwoTone, WarningTwoTone } from '@ant-design/icons'
import { apiScope } from '../../configuration/config'
import { createFetchInterceptor } from '../../common/api-utils/token-refresh-interceptor'
import { useTokenRefresh } from '../../common/hooks/use-token-refresh'
import { SessionTimeoutWarning } from '../../common/components/session-timeout-warning'
import { showErrorAlert } from '../../common/utils/session-alert'

interface AzureADWrapperProps {
  children: React.ReactNode
}

function ErrorComponent() {
  useEffect(() => {
    showErrorAlert('Authentication error occurred. Please try again.', 5000)
  }, [])

  return (
    <div className="flex flex-col md:flex-row w-full justify-center items-center gap-4 min-h-[40vh]">
      <div className="relative h-12 w-12">
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <WarningTwoTone className="text-3xl" twoToneColor="#00338E" />
        </div>
      </div>
      <div className="text-xl font-semibold ">
        An error occurred during authentication.
      </div>
    </div>
  )
}

function LoadingComponent() {
  return (
    <div className="flex flex-col md:flex-row w-full justify-center items-center gap-4 min-h-[40vh]">
      <div className="relative h-12 w-12">
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <LoadingOutlined className="text-5xl leading-[0px] text-theme-blue" />
        </div>
        <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
          <LockTwoTone className="text-2xl" twoToneColor="#00338E" />
        </div>
      </div>
      <div className="text-xl font-semibold ">Authentication in progress.</div>
    </div>
  )
}

//

const AuthenticatedApp: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance } = useMsal()

  useTokenRefresh()

  useEffect(() => {
    createFetchInterceptor(instance)

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      if (error && typeof error === 'object') {
        const errorMessage = error.message || ''
        if (
          errorMessage.includes('Failed to fetch') ||
          errorMessage.includes('401') ||
          errorMessage.includes('400') ||
          errorMessage.includes('Unauthorized')
        ) {
          // Interceptor will handle this
        }
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [instance])

  return (
    <>
      <SessionTimeoutWarning warningTime={5} sessionTimeout={55} />
      {children}
    </>
  )
}

const AzureADWrapper: React.FC<AzureADWrapperProps> = ({ children }) => {
  const loginRequest: ILoginRequest = {
    scopes: ['User.Read', 'User.Read.All', apiScope],
  }
  const authRequest = {
    ...loginRequest,
  }

  return (
    <AuthProvider>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={authRequest}
        errorComponent={ErrorComponent}
        loadingComponent={LoadingComponent}
      >
        <UserMiddleware>
          <AuthenticatedApp>{children}</AuthenticatedApp>
        </UserMiddleware>
      </MsalAuthenticationTemplate>
    </AuthProvider>
  )
}

export default AzureADWrapper
